import { inject } from '@angular/core';
import { ApiQuery } from '@kinet-open-network/client/core/api-client';
import {
  callApiMethod,
  setError,
  setLoaded,
  setLoading,
  withExport,
  withResourceState,
} from '@kinet-open-network/client/shared/data-access/state';
import { IInvoiceActionData } from '@kinet-open-network/shared/data-access/model';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { Invoice } from '../..';
import { IExportSipoData } from '../models/api/export-sipo.model';
import { InvoicesService } from '../services/invoices.service';
import { LiquidationsService } from '../services/liquidations.service';

export const InvoicesStore = signalStore(
  { protectedState: false },
  withResourceState(InvoicesService, {
    omit: ['items', 'ispData', 'address', 'totalVatItems', 'customer'],
  }),
  withExport(InvoicesService),
  withMethods(
    (
      store,
      service = inject(InvoicesService),
      liquidationService = inject(LiquidationsService),
    ) => ({
      generateItems: (id: string) =>
        callApiMethod(() => service.generateInvoiceItems(id), {}),
      generateCustomerData: (id: string) => {
        patchState(store, setLoading());
        return callApiMethod(() => service.generateCustomerData(id), {
          next: (invoice) => patchState(store, setLoaded(), { model: invoice }),
          error: (err) => patchState(store, setError(err)),
        });
      },
      downloadPdf: (id: string) => {
        return service.downloadPdf(id);
      },
      regeneratePdf: (id: string) => {
        patchState(store, setLoading());
        return callApiMethod(() => service.regeneratePdf(id), {
          next: () => patchState(store, setLoaded()),
          error: (err) => patchState(store, setError(err)),
        });
      },
      runAction: (data: IInvoiceActionData) => {
        patchState(store, setLoading());
        return callApiMethod(() => service.runAction(data), {
          next: () => patchState(store, setLoaded()),
          error: (err) => patchState(store, setError(err)),
        });
      },
      batchAction: (data: IInvoiceActionData, query?: ApiQuery | null) => {
        patchState(store, setLoading());
        return callApiMethod(() => service.batchAction(data, query), {
          next: () => patchState(store, setLoaded()),
          error: (err) => patchState(store, setError(err)),
        });
      },
      massDownload: (format: 'zip' | 'pdf', query?: ApiQuery | null) => {
        patchState(store, setLoading());
        return service.massDownload(format, query).then(
          (skipped) => {
            patchState(store, setLoaded());
            return skipped;
          },
          (err) => {
            patchState(store, setError(err));
            return 0;
          },
        );
      },
      exportSipo: async (data: IExportSipoData) => {
        patchState(store, setLoading());

        try {
          await service.exportSipo(data);
          patchState(store, setLoaded());
        } catch (error) {
          patchState(store, setError(error));
        }
      },
      removeLiquidation: async (liquidationId: string) => {
        patchState(store, setLoading());

        try {
          await firstValueFrom(liquidationService.delete(liquidationId));

          const invoice = store.model();

          const liquidations =
            invoice?.liquidations?.filter((l) => l.id !== liquidationId) ?? [];

          if (invoice?.id && liquidations) {
            patchState(store, setLoaded(), {
              model: new Invoice({
                ...invoice,
                liquidations,
              }),
            });
          }
        } finally {
          patchState(store, setLoaded());
        }
      },
    }),
  ),
);
